import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationStyles from './VariationStyles';

export const DIVIDERS = ['none', 'black'];

const DEFAULT_FONT_SIZE = 36;

export interface ImageVariation {
  imageSrc: string;
  watermarkTag: string;
  watermarkPosition: string;
  topText: string;
  topTextBrakes?: boolean;
  bottomText: string;
  bottomTextBrakes?: boolean;
  watermark?: boolean;
  fontSize?: number;
}

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const TextItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextAreaBox = styled.div`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
`;

const TopTextAreaBox = styled(TextAreaBox)`
  max-height: 246px;

  overflow-y: auto;
  ${scrollBarStyles}
`;

const StyledTextArea = styled(Textarea)`
  height: 90px;
`;

const TopTextArea = styled(Textarea)`
  height: 68px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 209px;
`;

const StyledUploadedImages = styled(UploadedImages)`
  max-height: 390px;
  min-height: 200px;
`;

const LabelHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ImagesBox = styled.div<{ $hasImages?: boolean }>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

const TextRight = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
`;

const StyledRemoveButton = styled(RemoveButton)`
  background: transparent;
  height: auto;
  padding: 0;
  margin-bottom: 8px;
  text-decoration: underline;
  transition: all 0.2s;
  &:hover {
    border-color: transparent;
    opacity: 0.8;
  }
`;

const CounterBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  width: 20px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddMoreHolder = styled.div<{ $inABox?: boolean }>`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  ${({ $inABox }) =>
    $inABox &&
    css`
      padding-bottom: 0;
      margin-left: -24px;
      margin-right: -24px;
      margin-bottom: -8px;
    `}
`;

const StyeldAddMoreButton = styled(AddMoreButton)`
  margin-left: auto;
`;

const BottomTextsHolder = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  padding-right: 8px;
  max-height: 397px;
  overflow-y: auto;
  ${scrollBarStyles}
`;

interface Props {
  inArticle?: boolean;
}

type TopTextItem = { text: string; brakes: boolean };

type BottomTextItem = {
  text: string;
  fontSize?: number;
  brakes: boolean;
  watermark: boolean;
};

const VariationMeme: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);

  const [topTexts, setTopTexts] = useState<TopTextItem[]>([
    { text: '', brakes: false },
  ]);

  const [bottomTexts, setBottomTexts] = useState<BottomTextItem[]>([
    { text: '', fontSize: DEFAULT_FONT_SIZE, brakes: false, watermark: true },
  ]);

  const [multipleVariations, setMultipleVariations] = useState(false);

  const [fullImages, setFullImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  // ---- Top text handlers ----
  const addTopText = () =>
    setTopTexts((prev) => [...prev, { text: '', brakes: false }]);

  const updateTopText = (idx: number, value: string) =>
    setTopTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, text: value } : t)),
    );

  const toggleTopTextBrakes = (idx: number) =>
    setTopTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, brakes: !t.brakes } : t)),
    );

  const removeTopText = (idx: number) =>
    setTopTexts((prev) => prev.filter((_, i) => i !== idx));

  // ---- Bottom text handlers ----
  const addBottomText = () =>
    setBottomTexts((prev) => [
      ...prev,
      { text: '', fontSize: DEFAULT_FONT_SIZE, brakes: false, watermark: true },
    ]);

  const updateBottomText = (idx: number, value: string) =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, text: value } : b)),
    );

  const updateBottomFontSize = (idx: number, size: string) =>
    setBottomTexts((prev) =>
      prev.map((b, i) =>
        i === idx
          ? {
              ...b,
              fontSize: size === '' ? undefined : parseInt(size) || undefined,
            }
          : b,
      ),
    );

  const toggleBottomTextBrakes = (idx: number) =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, brakes: !b.brakes } : b)),
    );

  const toggleBottomTextWatermark = (idx: number) =>
    setBottomTexts((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, watermark: !b.watermark } : b)),
    );

  const removeBottomText = (idx: number) =>
    setBottomTexts((prev) => prev.filter((_, i) => i !== idx));

  const onReset = () => {
    setFullImages([]);
    setVariations([]);
    setImages([]);
    setTopTexts([{ text: '', brakes: false }]);
    setBottomTexts([
      { text: '', fontSize: DEFAULT_FONT_SIZE, brakes: false, watermark: true },
    ]);
  };

  useEffect(() => {
    if (!multipleVariations) {
      setTopTexts((prev) => (prev.length > 1 ? [prev[0]] : prev));
      setBottomTexts((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, [multipleVariations]);

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const cleanedTopTexts: TopTextItem[] = topTexts
      .map((t) => ({ ...t, text: t.text.trim() }))
      .filter((t) => t.text.length);

    const newVariations: ImageVariation[] = fullImages.flatMap((image) => {
      const imageSrc = image.croppedImage ?? URL.createObjectURL(image.image);

      const tops = cleanedTopTexts.length
        ? cleanedTopTexts
        : [{ text: '', brakes: false }];

      return tops.flatMap((t) =>
        bottomTexts.map((b) => ({
          imageSrc,
          watermarkTag: getWatermarkTag(image),
          watermarkPosition: getWatermarkPosition(image),
          topText: t.text,
          topTextBrakes: t.brakes,
          bottomText: b.text,
          bottomTextBrakes: b.brakes,
          watermark: b.watermark,
          fontSize: b.fontSize,
        })),
      );
    });

    setVariations(newVariations);
  };

  const renderFullImages = fullImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setFullImages}
    />
  ));

  const isResetDisabled =
    !fullImages?.length && !topTexts.length && !bottomTexts.length;

  return (
    <>
      <VariationStyles />
      <Box>
        <Filler />
        <Header>
          <div>
            <Heading $type="button">Meme</Heading>
            <Text $type="smallHeadline" $color="gray">
              Add heading text alongside the cover and bottom text block
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>

        {showContent && (
          <>
            <TopTextAreaBox>
              {topTexts.map((item, idx) => (
                <TextItem key={idx}>
                  <div>
                    <LabelHolder>
                      <LabelText $type="smallHeadline">
                        Heading Text
                        {topTexts.length > 1 && (
                          <CounterBadge $type="extraSmall" $color="white">
                            {idx + 1}
                          </CounterBadge>
                        )}
                      </LabelText>
                      {topTexts.length > 1 && (
                        <StyledRemoveButton
                          color="red"
                          onClick={() => removeTopText(idx)}
                        >
                          Remove
                        </StyledRemoveButton>
                      )}
                    </LabelHolder>
                    <TopTextArea
                      onChange={(e) => updateTopText(idx, e.target.value)}
                      value={item.text}
                      placeholder="Add text"
                    />
                  </div>
                  <TextRight>
                    <Checkbox
                      checked={!!item.brakes}
                      onChange={() => toggleTopTextBrakes(idx)}
                    >
                      Custom brakes
                    </Checkbox>
                  </TextRight>
                </TextItem>
              ))}
            </TopTextAreaBox>
            {multipleVariations && (
              <AddMoreHolder>
                {topTexts.length > 1 && (
                  <AttachedCountBadge count={topTexts.length} text="Headings" />
                )}
                <StyeldAddMoreButton color="colorized" onClick={addTopText}>
                  Add heading text
                </StyeldAddMoreButton>
              </AddMoreHolder>
            )}
            <ImageGrid>
              <ImagesBox $hasImages={!!fullImages?.length}>
                <LabelText $type="smallHeadline">Images</LabelText>
                {!!fullImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderFullImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={fullImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setFullImages}
                  />
                )}
              </ImagesBox>
              <TextAreaBox>
                <BottomTextsHolder>
                  {bottomTexts.map((b, idx) => (
                    <TextItem key={idx}>
                      <div>
                        <LabelHolder>
                          <LabelText $type="smallHeadline">
                            Bottom Text
                            {bottomTexts.length > 1 && (
                              <CounterBadge $type="extraSmall" $color="white">
                                {idx + 1}
                              </CounterBadge>
                            )}
                          </LabelText>
                          {bottomTexts.length > 1 && (
                            <StyledRemoveButton
                              color="red"
                              onClick={() => removeBottomText(idx)}
                            >
                              Remove
                            </StyledRemoveButton>
                          )}
                        </LabelHolder>
                        <StyledTextArea
                          onChange={(e) =>
                            updateBottomText(idx, e.target.value)
                          }
                          value={b.text}
                          placeholder="Bottom text"
                        />
                      </div>
                      <div>
                        <LabelText $type="smallHeadline">
                          Font Size, px
                        </LabelText>
                        <Input
                          type="number"
                          min="0"
                          value={b.fontSize ?? ''}
                          onChange={(e) =>
                            updateBottomFontSize(idx, e.target.value)
                          }
                          placeholder="Add Font Size"
                        />
                      </div>
                      <TextRight>
                        <Checkbox
                          checked={!!b.brakes}
                          onChange={() => toggleBottomTextBrakes(idx)}
                        >
                          Custom brakes
                        </Checkbox>
                        <Checkbox
                          checked={!!b.watermark}
                          onChange={() => toggleBottomTextWatermark(idx)}
                        >
                          Bored Panda Watermark
                        </Checkbox>
                      </TextRight>
                    </TextItem>
                  ))}
                </BottomTextsHolder>
                {multipleVariations && (
                  <AddMoreHolder $inABox>
                    {bottomTexts.length > 1 && (
                      <AttachedCountBadge
                        count={bottomTexts.length}
                        text="Bottom Texts"
                      />
                    )}
                    <StyeldAddMoreButton
                      color="colorized"
                      onClick={addBottomText}
                    >
                      Add bottom text
                    </StyeldAddMoreButton>
                  </AddMoreHolder>
                )}
              </TextAreaBox>
            </ImageGrid>
          </>
        )}
      </Box>

      {showContent && (
        <ButtonHolder>
          <Button disabled={!fullImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}

      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />

      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setFullImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationMeme;
